import {
  Box,
  HoverCard,
  rem,
  Text,
  useComputedColorScheme,
} from "@mantine/core";
import { IconSparkles, IconX } from "@tabler/icons-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useJustAddedIds } from "@/shared/hooks/useJustAddedIds";
import {
  getTagColorPair,
  type TagColor,
} from "@/design-system/tokens/tagColors";
import classes from "./TagChip.module.css";

export type TagChipModel = {
  documentId: string;
  name: string;
  color: TagColor;
  /**
   * SA-1168: etiqueta aplicada pela IA e ainda ativa. Fica NO MODELO (e não como
   * prop de cada tela) para o selo aparecer em toda superfície que renderiza a
   * atribuição — lista de chats, header, lista de contatos e painel.
   */
  appliedByAi?: boolean;
};

type TagChipSize = "xs" | "sm" | "md";

const PADDING: Record<TagChipSize, string> = {
  xs: `${rem(2)} ${rem(6)}`,
  sm: `${rem(3)} ${rem(8)}`,
  md: `${rem(4)} ${rem(10)}`,
};

const FONT_SIZE: Record<TagChipSize, string> = {
  xs: rem(10),
  sm: rem(11),
  md: rem(13),
};

/** Selo da IA proporcional ao tamanho do chip (SA-1168). */
const AI_ICON_SIZE: Record<TagChipSize, number> = { xs: 10, sm: 11, md: 13 };

export const AI_TAG_BADGE_LABEL = "Aplicada pelo Agente de IA";

export function TagChip({
  tag,
  size = "sm",
  onRemove,
  truncate = false,
  removeVariant = "inline",
  byAi = false,
  justAdded = false,
}: {
  tag: TagChipModel;
  size?: TagChipSize;
  onRemove?: () => void;
  truncate?: boolean;
  /** "inline": X ao lado do texto. "corner": X no canto superior direito. */
  removeVariant?: "inline" | "corner";
  /**
   * SA-1168: etiqueta aplicada pela IA — mostra o selo de brilho à esquerda do
   * nome. Sem a prop o chip é idêntico ao de antes (zero regressão visual).
   */
  byAi?: boolean;
  /**
   * Chip que ENTROU agora na lista (ex.: a IA etiquetou e o websocket trouxe):
   * anima a entrada em vez de aparecer seco. Só quem sabe o antes/depois da
   * lista pode decidir isso — ver `useJustAddedIds`.
   */
  justAdded?: boolean;
}) {
  const scheme = useComputedColorScheme("light");
  const pair = getTagColorPair(tag.color, scheme === "dark" ? "dark" : "light");

  const isCornerRemove = removeVariant === "corner" && Boolean(onRemove);
  const className = [
    isCornerRemove ? classes.chipHoverable : null,
    justAdded ? classes.chipEnter : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      className={className || undefined}
      data-just-added={justAdded ? "true" : undefined}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: rem(4),
        background: pair.bg,
        color: pair.fg,
        padding: PADDING[size],
        borderRadius: rem(999),
        fontSize: FONT_SIZE[size],
        fontWeight: 600,
        lineHeight: 1,
        maxWidth: truncate ? rem(140) : undefined,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}>
      {byAi && (
        // Rótulo/tooltip no wrapper (um só nó acessível) e ícone decorativo.
        // Cor = `fg` do próprio chip, não um violeta fixo como no protótipo: o
        // selo aparece sobre QUALQUER cor de etiqueta e só a cor do par garante
        // contraste AA nos dois esquemas — é a mesma cor do texto ao lado.
        <Box
          component="span"
          role="img"
          aria-label={AI_TAG_BADGE_LABEL}
          title={AI_TAG_BADGE_LABEL}
          style={{ display: "inline-flex", flexShrink: 0, lineHeight: 0 }}>
          <IconSparkles
            size={AI_ICON_SIZE[size]}
            color={pair.fg}
            aria-hidden
          />
        </Box>
      )}
      <Text
        component="span"
        fz={FONT_SIZE[size]}
        fw={600}
        c={pair.fg}
        style={{
          overflow: truncate ? "hidden" : undefined,
          textOverflow: truncate ? "ellipsis" : undefined,
          lineHeight: 1,
        }}>
        {tag.name}
      </Text>
      {onRemove && (
        <Box
          component="button"
          type="button"
          aria-label={`Remover ${tag.name}`}
          className={isCornerRemove ? classes.cornerRemove : undefined}
          // stopPropagation: em contextos clicáveis (item da lista de chats) o
          // clique no X não deve selecionar/abrir o chat, só remover a tag.
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onRemove();
          }}
          style={
            removeVariant === "corner"
              ? {
                  position: "absolute",
                  top: rem(-4),
                  right: rem(-4),
                  width: rem(11),
                  height: rem(11),
                  borderRadius: "50%",
                  background: pair.fg,
                  color: pair.bg,
                  border: `${rem(1)} solid ${pair.bg}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  padding: 0,
                  lineHeight: 0,
                }
              : {
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: pair.fg,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }
          }>
          <IconX
            size={removeVariant === "corner" ? 7 : 12}
            stroke={removeVariant === "corner" ? 3 : 2}
          />
        </Box>
      )}
    </Box>
  );
}

/** Espera o redimensionamento assentar antes de remedir a linha. */
const RESIZE_SETTLE_MS = 120;

export function TagChipsRow({
  tagIds,
  tags,
  maxVisible = 1,
  size = "xs",
  trailing,
  onRemove,
  removeVariant = "inline",
}: {
  tagIds: string[];
  tags: Record<string, TagChipModel>;
  /** Número fixo de chips visíveis, ou "auto" para caber em uma linha. */
  maxVisible?: number | "auto";
  size?: TagChipSize;
  /**
   * Elemento renderizado DENTRO da linha, logo após as tags/"+N" (ex.: o botão
   * "+"). No modo "auto" ele entra na medição, então as tags nunca ficam por
   * baixo dele e ele cola nas tags em vez de ir para a borda direita.
   */
  trailing?: ReactNode;
  /** Quando definido, cada chip visível ganha um X que remove aquela tag. */
  onRemove?: (tagId: string) => void;
  removeVariant?: "inline" | "corner";
}) {
  const isAuto = maxVisible === "auto";
  const removable = Boolean(onRemove);
  // Só o chip que entrou DEPOIS da primeira pintura anima (rolar a lista de
  // chats remonta itens: animar na montagem faria a tela piscar).
  const justAddedIds = useJustAddedIds(tagIds);
  const rowRef = useRef<HTMLDivElement>(null);
  // Modo "auto": reduz a quantidade visível até caber em UMA linha (sem quebrar);
  // o restante vira "+N". Recalcula no resize (ex.: painel de detalhes abrindo).
  const [autoVisible, setAutoVisible] = useState(tagIds.length);
  const [measureTick, setMeasureTick] = useState(0);

  // Recomeça do total quando as tags mudam.
  useLayoutEffect(() => {
    if (isAuto) setAutoVisible(tagIds.length);
  }, [isAuto, tagIds.length]);

  // No resize do container, volta ao total e força nova medição. O "tick"
  // garante a remedição mesmo quando já estava no total (ex.: ao estreitar,
  // onde setar o mesmo valor sofreria bail-out e não remediria).
  //
  // A remedição ESPERA o resize assentar, e é o que separa isto de um gargalo:
  // esta linha é renderizada uma vez por conversa, e o laço abaixo mede layout
  // de forma síncrona até caber. Disparando por frame, recolher a sidebar ou
  // arrastar a divisa custava (itens x chips x frames) medições (SA-1278).
  // Ignora também resize que não mexeu na LARGURA, que é a única dimensão que
  // muda quantos chips cabem.
  useLayoutEffect(() => {
    if (!isAuto) return;
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    let lastWidth = Math.round(el.getBoundingClientRect().width);
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const observer = new ResizeObserver((entries) => {
      const width = Math.round(
        entries[0]?.contentRect.width ?? el.getBoundingClientRect().width
      );
      if (width === lastWidth) return;
      lastWidth = width;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        setAutoVisible(tagIds.length);
        setMeasureTick((t) => t + 1);
      }, RESIZE_SETTLE_MS);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(settleTimer);
    };
  }, [isAuto, tagIds.length]);

  // Reduz até caber (o "+N" entra na conta porque é renderizado ao cortar).
  useLayoutEffect(() => {
    if (!isAuto) return;
    const el = rowRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth && autoVisible > 1) {
      setAutoVisible((c) => c - 1);
    }
  }, [isAuto, autoVisible, measureTick, tagIds.length]);

  // Sem tags renderiza nada — exceto quando há um `trailing` (ex.: o botão "+"),
  // que precisa aparecer mesmo sem tags (e em posição estável, ver keys abaixo).
  if (tagIds.length === 0 && !trailing) {
    return null;
  }

  const effectiveMax = maxVisible === "auto" ? autoVisible : maxVisible;
  const visible = tagIds.slice(0, effectiveMax);
  const hidden = tagIds.slice(effectiveMax);

  return (
    <Box
      ref={isAuto ? rowRef : undefined}
      style={{
        display: isAuto ? "flex" : "inline-flex",
        gap: rem(4),
        alignItems: "center",
        ...(isAuto
          ? {
              flex: 1,
              minWidth: 0,
              // No modo removível o overflow precisa ficar visível para o X do
              // canto não ser cortado (a medição usa scrollWidth e ignora o X,
              // que é absolute — então a contagem de "+N" segue correta).
              overflow: removable ? "visible" : "hidden",
              flexWrap: "nowrap",
            }
          : {}),
      }}>
      {visible.map((id) =>
        tags[id] ? (
          <TagChip
            key={id}
            tag={tags[id]}
            size={size}
            truncate
            onRemove={onRemove ? () => onRemove(id) : undefined}
            removeVariant={removeVariant}
            byAi={tags[id].appliedByAi}
            justAdded={justAddedIds.has(id)}
          />
        ) : null
      )}
      {hidden.length > 0 && (
        <HoverCard
          key="__overflow__"
          width={220}
          shadow="md"
          openDelay={120}
          position="top"
          withArrow>
          <HoverCard.Target>
            <Box
              style={{
                background: "var(--mantine-color-background_1-0)",
                color: "var(--mantine-color-plain_text-0)",
                padding: PADDING[size],
                borderRadius: rem(999),
                fontSize: FONT_SIZE[size],
                fontWeight: 700,
                lineHeight: 1,
                opacity: 0.7,
                cursor: "default",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}>
              +{hidden.length}
            </Box>
          </HoverCard.Target>
          <HoverCard.Dropdown p={rem(8)}>
            <Box style={{ display: "flex", flexWrap: "wrap", gap: rem(4) }}>
              {hidden.map((id) =>
                tags[id] ? (
                  <TagChip
                    key={id}
                    tag={tags[id]}
                    size="xs"
                    byAi={tags[id].appliedByAi}
                  />
                ) : null
              )}
            </Box>
          </HoverCard.Dropdown>
        </HoverCard>
      )}
      {trailing && (
        // key estável: a posição muda conforme entram/saem tags e o "+N"; sem
        // key, a reconciliação por índice remontaria o trailing (ex.: o Popover
        // do botão "+" fecharia ao adicionar a 1ª tag).
        <Box
          key="__trailing__"
          style={{ display: "inline-flex", flexShrink: 0 }}>
          {trailing}
        </Box>
      )}
    </Box>
  );
}
